import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewChildren } from '@angular/core';
import { select, Store } from '@ngrx/store';
import * as LiftReducer from '@app/lift/store/lifts.reducer';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { Tray } from '@models/tray.model';
import { ActivatedRoute, Router } from '@angular/router';
import * as LiftActions from '@app/lift/store/lifts.actions';
import { combineLatest, Subscription, Observable } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Slot } from '@models/slot.model';
import { Tool } from '@app/tool/model/tool.model';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { Group } from '@app/tool/model/group.model';
import { ToolSet } from '@app/tool/model/toolSet.model';
import { Batch } from '@models/batch.model';
import { AddToBatchModalComponent } from '@app/lift/components/add-batch-modal/add-to-batch-modal.component';
import { RemoveFromBatchModalComponent } from '@app/lift/components/remove-batch-modal/remove-from-batch-modal.component';
import { ConfirmationModalComponent } from '@app/shared/components';
import { EditSlotModalComponent } from '@app/lift/components/edit-slot-modal/edit-slot-modal.component';
import { RenameCartModalComponent } from '@app/lift/components/rename-cart-modal/rename-cart-modal.component';
import { TrayType } from '@models/enums/tray-type.enum';
import { EditBatchComponent } from '@app/lift/components/edit-batch/edit-batch.component';
import { EditTrayModalComponent } from '@app/lift/components/edit-tray-modal/edit-tray-modal.component';
import { CopySlotModalComponent } from '@app/lift/components/copy-slot-modal/copy-slot-modal.component';
import { CopyBatchesModalComponent } from '@app/lift/components/copy-batches-modal/copy-batches-modal.component';
import { SlotComponent } from '@app/lift/components/slot/slot.component';
import { BatchComponent } from '@app/lift/components/batch/batch.component';
import * as _ from "lodash";
import { Log } from "ng2-logger";
import { Line } from '@models/line.model';
import { Actions, ofType } from '@ngrx/effects';


@Component({
    selector: 'app-tray-details',
    templateUrl: './tray-details.component.html',
    styleUrls: ['./tray-details.component.scss']
})
export class TrayDetailsComponent implements OnInit, OnDestroy {
    _logger = Log.create(TrayDetailsComponent.name);

    @ViewChild(AddToBatchModalComponent, {static: true}) addToBatchModal: AddToBatchModalComponent;
    @ViewChild(RemoveFromBatchModalComponent, {static: true}) removeFromBatchModal: RemoveFromBatchModalComponent;
    @ViewChild(ConfirmationModalComponent, {static: true}) confirmationModal: ConfirmationModalComponent;
    @ViewChild('deleteSlotConfirmationModal', {static: true}) deleteSlotConfirmationModal: ConfirmationModalComponent;
    @ViewChild(EditSlotModalComponent, {static: true}) editSlotModal: EditSlotModalComponent;
    @ViewChild(CopySlotModalComponent, {static: true}) copySlotModal: CopySlotModalComponent;
    @ViewChild(CopyBatchesModalComponent, {static: true}) copyBatchesModal: CopyBatchesModalComponent;
    @ViewChild(RenameCartModalComponent, {static: true}) updateCartModal: RenameCartModalComponent;
    @ViewChild(EditBatchComponent, {static: true}) editBatchModal: EditBatchComponent;
    @ViewChild(EditTrayModalComponent, {static: true}) editTrayModal: EditTrayModalComponent;
    @ViewChild('errorModal', {static: true}) errorModal: ConfirmationModalComponent;

    @ViewChildren(SlotComponent) slots: SlotComponent[];
    @ViewChildren(BatchComponent) batches: BatchComponent[];

    isLoading$: Observable<boolean>;
    isLoadingTools$: Observable<boolean>;
    tray$: Observable<Tray>;
    updatedTray$: Observable<Tray>;
    slot$: Observable<Slot>;
    tools$: Observable<any>;
    batch$: Observable<Batch>;
    updatedBatch$: Observable<Batch>;
    deletedBatch$: Observable<Batch>;
    createdBatch$: Observable<Batch>;
    deletedSlot$: Observable<Slot>;
    updatedSlot$: Observable<Slot>;
    addedSlot$: Observable<Slot>;
    error$: Observable<any>;
    lines$: Observable<Line[]>;
    visibleSlots: Slot[] = [];
    filtersExpanded = true;
    filteringSlots = true;
    trayRequestPending = true;
    filterError = false;
    trayLoadFailed = false;
    selectingInitialBatch = false;
    filterForm = new UntypedFormGroup({
        toolUID: new UntypedFormControl(null),
        toolName: new UntypedFormControl(null),
        line: new UntypedFormControl(null)
    });
    private lines: Line[] = [];
    private filterSubscriptions = new Subscription();

    traySubscription: Subscription;
    toolsSubscription: Subscription;
    batchSubscription: Subscription;
    batchUpdateSubscription: Subscription;
    slotSubscription: Subscription;
    deleteBatchSubscription: Subscription;
    newBatchSubscription: Subscription;
    createdBatchSubscription: Subscription;
    deleteSlotSubscription: Subscription;
    updateSlotSubscription: Subscription;
    addedSlotSubscription: Subscription;
    updateBatchSubscription: Subscription;
    removeFromSubscription: Subscription;
    errorSubscription: Subscription;
    updatedTraySubscription: Subscription;

    toolConfig = new TableConfig();
    batchConfig = new TableConfig();

    liftId: number;
    trayId: number;
    selectedTray: Tray;
    public tools: Array<Group>;
    public selectedTools: Array<Tool | ToolSet> = new Array<Tool | ToolSet>();
    selectedSlot: Slot;
    selectedBatch: Batch;
    toDeleteBatch: Batch;
    toDeleteSlot: Slot;
    errorMessagePopup: any;

    constructor(private store: Store<any>,
                private cdr: ChangeDetectorRef,
                private route: ActivatedRoute,
                private router: Router,
                private actions$: Actions) {

        this.isLoading$ = this.store.pipe(select(LiftReducer.isLoading));
        this.isLoadingTools$ = this.store.pipe(select(LiftReducer.isLoadingTools));
        this.tray$ = this.store.pipe(select(LiftReducer.getTray), filter(tray => tray !== null));
        this.updatedTray$ = this.store.pipe(select(LiftReducer.getUpdatedTray), filter(tray => tray !== null));
        this.slot$ = this.store.pipe(select(LiftReducer.getSlot));
        this.tools$ = this.store.pipe(select(LiftReducer.getTools), filter(tools => tools !== null));
        this.batch$ = this.store.pipe(select(LiftReducer.getBatch));
        this.updatedBatch$ = this.store.pipe(select(LiftReducer.getUpdatedBatch));
        this.createdBatch$ = this.store.pipe(select(LiftReducer.getCreatedBatch));
        this.deletedBatch$ = this.store.pipe(select(LiftReducer.getDeletedBatch));
        this.deletedSlot$ = this.store.pipe(select(LiftReducer.getDeletedSlot));
        this.updatedSlot$ = this.store.pipe(select(LiftReducer.getUpdatedSlot));
        this.addedSlot$ = this.store.pipe(select(LiftReducer.getAddedSlot));
        this.error$ = this.store.pipe(select(LiftReducer.getError));
        this.lines$ = this.store.pipe(select(LiftReducer.getLines));

        this.filterSubscriptions.add(this.lines$.subscribe(lines => {
            this.lines = lines || [];
            const selected = this.filterForm.value.line;
            const line = selected && this.lines.find(item => item.id === selected.id);
            if (line) {
                this.filterForm.patchValue({line}, {emitEvent: false});
            }
        }));
        this.filterSubscriptions.add(this.filterForm.valueChanges.pipe(debounceTime(300)).subscribe(value => {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {toolUID: value.toolUID?.trim() || null, toolName: value.toolName?.trim() || null,
                    lineId: value.line?.id || null},
                queryParamsHandling: 'merge', replaceUrl: true
            });
        }));
        this.store.dispatch(new LiftActions.LoadLinesAction());

        this.filterSubscriptions.add(this.actions$.pipe(ofType(LiftActions.LOAD_TRAY_DETAILS_FAILED)).subscribe(() => {
            this.trayRequestPending = false;
            this.filteringSlots = false;
            this.trayLoadFailed = true;
        }));
        // Get tray details based on path liftId and trayId
        this.filterSubscriptions.add(combineLatest([this.route.params, this.route.queryParamMap]).pipe(
            map(([params, query]) => ({id: +params['id'], trayId: +params['trayId'],
                toolUID: query.get('toolUID') || undefined, toolName: query.get('toolName') || undefined,
                lineId: Number(query.get('lineId')) || undefined})),
            distinctUntilChanged((a, b) => a.id === b.id && a.trayId === b.trayId && a.toolUID === b.toolUID
                && a.toolName === b.toolName && a.lineId === b.lineId)
        ).subscribe(filters => {
                this.filteringSlots = true;
                this.trayRequestPending = true;
                this.trayLoadFailed = false;
                this.selectedTray = null;
                this.selectedSlot = null;
                this.selectedBatch = null;
                this.selectedTools = [];
                this.visibleSlots = [];
                this.liftId = filters.id;
                this.trayId = filters.trayId;
                const currentLine = this.filterForm.value.line;
                const line = this.lines.find(item => item.id === filters.lineId) ||
                    (filters.lineId ? {id: filters.lineId, name: ''} : currentLine?.id == null ? currentLine : null);
                this.filterForm.patchValue({toolUID: filters.toolUID || null, toolName: filters.toolName || null, line},
                    {emitEvent: false});
                this.store.dispatch(new LiftActions.LoadTrayDetailsAction(this.liftId, this.trayId, filters));
        }));

        // Select the first slot from the tray by default
        this.traySubscription = this.tray$.subscribe(tray => {
            if (tray.id !== this.trayId) { return; }
            this.filteringSlots = false;
            this.trayRequestPending = false;
            this.trayLoadFailed = false;
            this.filterError = false;
            this.selectedTray = tray;
            this.visibleSlots = tray.slots || [];
            if (!this.visibleSlots.some(slot => slot.id === this.selectedSlot?.id)) {
                this.selectedSlot = null;
                this.selectedBatch = null;
                this.selectedTools = [];
                if (this.visibleSlots.length) {
                    this.onSlotSelect(this.visibleSlots[0]);
                }
            }
        });

        this.updatedTraySubscription = this.updatedTray$.pipe(filter(batch => null !== batch))
            .subscribe(() => {
                if (this.editTrayModal) {
                    this.editTrayModal.close();
                }
            })

        this.toolsSubscription = this.tools$.subscribe(tools => {
            if (tools) {
                this.tools = _.cloneDeep(tools.tools);
                this.toolConfig = {
                    ...this.toolConfig,
                    pager: {
                        ...this.toolConfig.pager,
                        count: tools.count // modify count property
                    }
                } as TableConfig;
            }
        });

        this.batchSubscription = this.batch$.subscribe(batch => {
            if (batch) {
                this.selectingInitialBatch = false;
                this.selectedBatch = _.cloneDeep(batch);
                this.batchConfig = {
                    ...this.batchConfig,
                    pager: {
                        ...this.batchConfig.pager,
                        count: (batch.tools as any) ? (batch.tools as any).count : 0
                    }
                } as TableConfig
            }
        });

        this.batchUpdateSubscription = this.updatedBatch$.pipe()
            .subscribe(updatedBatch => {
                if (updatedBatch) {
                    this.selectedBatch = _.cloneDeep(updatedBatch);
                    this.batchConfig = {
                        ...this.batchConfig,
                        pager: {
                            ...this.batchConfig.pager,
                            count: (this.selectedBatch.tools as any)
                                ? (this.selectedBatch.tools as any).count
                                : 0
                        }
                    } as TableConfig
                }
                if (this.editBatchModal) {
                    this.editBatchModal.close();
                }
            });

        this.createdBatchSubscription = this.createdBatch$.pipe(filter(batch => null !== batch))
            .subscribe(batch => {
                this.onSelectBatch(batch);
            });

        this.slotSubscription = this.slot$.pipe(filter(slot => null !== slot))
            .subscribe(slot => {
                if (!this.filteringSlots && !this.visibleSlots.some(item => item.id === slot.id)) {
                    return;
                }
                this.selectedSlot = slot;
                if (this.selectedSlot && this.selectedSlot.batches && this.selectedSlot.batches.length > 0) {
                    this.selectingInitialBatch = true;
                    this.onSelectBatch(this.selectedSlot.batches[0]);
                } else {
                    this.selectingInitialBatch = false;
                    this.onSelectAllTools(this.toolConfig); // Tools tab
                }
            });

        this.deleteSlotSubscription = this.deletedSlot$.pipe(filter(slot => null !== slot))
            .subscribe(slot => {
                if (this.editSlotModal) {
                    this.editSlotModal.close();
                }
            });

        this.updateSlotSubscription = this.updatedSlot$.pipe(filter(slot => null !== slot))
            .subscribe(slot => {
                if (this.editSlotModal) {
                    this.editSlotModal.close();
                }
            })

        this.addedSlotSubscription = this.addedSlot$.pipe(filter(slot => null !== slot))
            .subscribe(slot => {
                if (this.editSlotModal) {
                    this.editSlotModal.close();
                }
            })

        this.errorSubscription = this.error$.pipe(filter(error => null !== error && null != error.error))
            .subscribe(error => {
                // 417 status code -- solution to decide the type of errors that will be displayed in a popup
                if (error.error && error.error.message && error.status === 417 && this.errorModal) {
                    this.errorMessagePopup = error.error.message;
                    this.errorModal.open();
                }
            });
    }

    ngOnInit() {
    }

    toggleFilters() {
        this.filtersExpanded = !this.filtersExpanded;
    }


    onSelectAllTools(config) {
        if (this.selectedBatch !== null) {
            this.selectedTools = [];
        }
        this.selectedBatch = null;
        this.store.dispatch(new LiftActions.LoadAllToolsAction(config));
    }

    onSlotSelect(slot: Slot) {
        // prevent refresh
        if (this.selectedSlot) {
            if (slot.id === this.selectedSlot.id) {
                return;
            }
        }

        this.selectedTools = [];
        this.selectedBatch = null;
        const value = this.filterForm.value;
        this.store.dispatch(new LiftActions.LoadSlotAction(this.liftId, this.trayId, slot.id, {
            toolUID: value.toolUID?.trim() || undefined,
            toolName: value.toolName?.trim() || undefined,
            lineId: value.line?.id || undefined
        }));
    }

    // Events
    onToolsPageChange(tableState) {
        this.onSelectAllTools(tableState);
    }

    onToolsSortChange(tableState) {
        this.onSelectAllTools(tableState);
    }

    onToolsFilterChange(tableState) {
        this.onSelectAllTools(tableState);
    }

    onToolsFilterGroupChange(tableState) {
    }

    onToolsGroupChange(tableState) {
        this.onSelectAllTools(tableState);
    }

    // Events
    onBatchesPageChange(tableState, batchId) {
        this.store.dispatch(new LiftActions.LoadBatchAction(batchId, tableState));
    }

    onBatchesSortChange(tableState, batchId) {
        this.store.dispatch(new LiftActions.LoadBatchAction(batchId, tableState));
    }

    onBatchesFilterChange(tableState, batchId) {
        this.store.dispatch(new LiftActions.LoadBatchAction(batchId, tableState));
    }

    onToolClicked(toolDetails: { checked: boolean, group: Tool | ToolSet }) {
        if (toolDetails.checked) {
            this.selectedTools.push(toolDetails.group);
        } else {
            const index = this.selectedTools.findIndex(item => item.id === toolDetails.group.id)
            if (index > -1) {
                this.selectedTools.splice(index, 1);
            }
        }
        this.cdr.detectChanges();
        this._logger.info("onToolClicked:: Selected tools", this.selectedTools);
    }


    onSelectBatch(batch: Batch) {
        // prevent refresh
        if (this.selectedBatch) {
            if (batch.id === this.selectedBatch.id) {
                return;
            }
        }

        this.selectedTools = [];
        this.toolConfig = new TableConfig();
        this.batchConfig = new TableConfig();
        this.store.dispatch(new LiftActions.LoadBatchAction(batch.id, this.toolConfig));
    }

    onSettingsClicked(event: any) {
        if (event.slot !== undefined) {
            this.onSlotSelect(event.slot);
        } else if (event.batch !== undefined) {
            this.onSelectBatch(event.batch);
        }
    }

    openCopyBatchesModal() {
        this.copyBatchesModal.open(this.liftId, this.trayId, this.selectedSlot.batches);
    }

    openAddToBatchModal() {
        if (!this.selectedTools.length) {
            return;
        }
        this.selectedTools = [...this.selectedTools];
        this.addToBatchModal.open();
    }

    openRemoveFromBatchModal() {
        if (!this.selectedTools.length) {
            return;
        }
        this.selectedTools = [...this.selectedTools];
        this.removeFromBatchModal.open();
    }

    openUpdateCartModal() {
        if (!this.selectedTools.length) {
            return;
        }
        this.selectedTools = [...this.selectedTools];
        this.updateCartModal.open();
    }

    addToolsToBatch(event: any) {
        this.store.dispatch(new LiftActions.AddToolsToBatchAction(event.tools, event.batchId, event.withStorage));

        this.updateBatchSubscription =
            this.store.pipe(select(LiftReducer.getUpdatedBatch), filter(batch => batch !== null))
                .subscribe((batch) => {
                    this.addToBatchModal.close();
                    this.resetSelectedTools();
                });
    }

    addToolsToNewBatch(event: any) {
        this.store.dispatch(new LiftActions.CreateBatchWithToolsAction(this.selectedSlot.id, event.tools, event.batchName,
            event.withStorage));

        this.createdBatchSubscription =
            this.store.pipe(select(LiftReducer.getCreatedBatch), filter(batch => batch !== null)).subscribe((batch) => {
                this.addToBatchModal.close();
                this.resetSelectedTools();
            });
    }

    removeFromBatch(event: any) {
        this.store.dispatch(new LiftActions.RemoveToolsFromBatchAction(event.tools, event.batchId));
        this.removeFromSubscription =
            this.store.pipe(select(LiftReducer.getUpdatedBatch), filter(batch => batch !== null)).subscribe((batch) => {
                this.removeFromBatchModal.close();
                this.resetSelectedTools();
            });
    }

    updateCart(event: any) {
        this.store.dispatch(new LiftActions.UpdateCartBatchToolsAction(event.tools, event.batchId, event.cart));
        this.updateBatchSubscription =
            this.store.pipe(select(LiftReducer.getUpdatedBatch), filter(batch => batch !== null)).subscribe((batch) => {
                this.updateCartModal.close();
                this.resetSelectedTools();
            });
    }

    resetSelectedTools() {
        this.selectedTools = [];
    }

    onRemoveBatch(batch: Batch) {
        this.toDeleteBatch = batch;
        this.confirmationModal.open();
    }

    onEditTrayClick() {
        this.editTrayModal.open(this.selectedTray);
    }

    onEditTray(tray) {
        this.store.dispatch(new LiftActions.UpdateTrayAction(this.liftId, tray));
    }

    onEditBatchClick(batch: Batch) {
        this.editBatchModal.open(batch);
    }

    onDuplicateBatchClick(batch: Batch) {
        this.store.dispatch(new LiftActions.DuplicateBatchAction(this.selectedSlot.id, batch.id));
    }

    onBatchSettingsClick(batch: Batch) {
        this.onSelectBatch(batch);
        this.batches.forEach((batchComponent) => {
            if (batch.id !== batchComponent.batch.id) {
                batchComponent.closeContextMenu();
            }
        });
        this.slots.forEach(slot => {
            slot.closeContextMenu();
        });
    }

    onEditBatch(event) {
        this.store.dispatch(new LiftActions.UpdateBatchAction(event.data));
    }

    onConfirmRemoveBatch() {
        this.store.dispatch(new LiftActions.DeleteBatchAction(this.toDeleteBatch.id));

        this.deleteBatchSubscription = this.deletedBatch$.pipe(filter(deleted => null !== deleted))
            .subscribe(deleted => {
                this.confirmationModal.close();
            });

    }

    onConfirmRemoveSlot() {
        this.store.dispatch(new LiftActions.DeleteSlotAction(this.liftId, this.trayId, this.toDeleteSlot.id));

        this.deleteSlotSubscription = this.deletedSlot$.pipe(filter(deleted => null !== deleted))
            .subscribe(_ => {
                this.deleteSlotConfirmationModal.close();
            });

    }

    onSlotEditClick(slot: Slot) {
        this.editSlotModal.open(slot);
    }

    onSlotDeleteClick(slot: Slot) {
        this.toDeleteSlot = slot;
        this.deleteSlotConfirmationModal.open(slot);
    }

    onSlotDuplicateClick(slot: Slot) {
        this.store.dispatch(
            new LiftActions.DuplicateSlotAction(this.liftId, this.trayId, slot.id)
        );
    }

    onSlotSettingsClick(slot: Slot) {
        this.onSlotSelect(slot);
        this.slots.forEach((slotComponent) => {
            if (slot.id !== slotComponent.slot.id) {
                slotComponent.closeContextMenu();
            }
        });
        this.batches.forEach(batch => {
            batch.closeContextMenu();
        });
    }

    onSlotCopyClick(slot: Slot) {
        this.copySlotModal.open(slot, this.liftId, this.trayId);
    }

    onEditSlot(event) {
        switch (event.operation) {
            case 'add':
                this.store.dispatch(new LiftActions.AddSlotAction(this.liftId, this.trayId, event.data));
                break;
            case 'edit':
                this.store.dispatch(new LiftActions.RenameSlotAction(this.liftId, this.trayId, event.data));
                break;
            case 'delete':
                this.store.dispatch(new LiftActions.DeleteSlotAction(this.liftId, this.trayId, event.data));
                break;
        }
    }

    onCreateNewSlotClick() {
        this.editSlotModal.open(null);
    }

    getName(tray: Tray) {
        if (!tray) {
            return null;
        }

        if (tray && tray.trayType === TrayType.Service) {
            return `${tray.name} (Service)`;
        }
        return tray.name;
    }

    ngOnDestroy(): void {
        this.filterSubscriptions.unsubscribe();
        if (this.traySubscription) {
            this.traySubscription.unsubscribe();
        }
        if (this.batchSubscription) {
            this.batchSubscription.unsubscribe();
        }
        if (this.toolsSubscription) {
            this.toolsSubscription.unsubscribe();
        }

        if (this.slotSubscription) {
            this.slotSubscription.unsubscribe();
        }

        if (this.deleteBatchSubscription) {
            this.deleteBatchSubscription.unsubscribe();
        }

        if (this.createdBatchSubscription) {
            this.createdBatchSubscription.unsubscribe();
        }

        if (this.updateSlotSubscription) {
            this.updateSlotSubscription.unsubscribe();
        }

        if (this.deleteSlotSubscription) {
            this.deleteSlotSubscription.unsubscribe();
        }

        if (this.addedSlotSubscription) {
            this.addedSlotSubscription.unsubscribe();
        }

        if (this.newBatchSubscription) {
            this.newBatchSubscription.unsubscribe();
        }

        if (this.updateBatchSubscription) {
            this.updateBatchSubscription.unsubscribe();
        }

        if (this.removeFromSubscription) {
            this.removeFromSubscription.unsubscribe();
        }

        if (this.errorSubscription) {
            this.errorSubscription.unsubscribe();
        }

        if (this.updatedTraySubscription) {
            this.updatedTraySubscription.unsubscribe();
        }
    }
}

